import { useEffect, useState } from 'react';

/**
 * Estado do bloco do Google no placement da grade, lido só de sinais oficiais.
 *
 * Estados do AdSense (`ESTADOS_ADSENSE`):
 *   loading         ainda sem resposta. O espaço é do Google (reservado, rótulo "Anúncio").
 *   filled          `data-ad-status="filled"`: o Google preencheu. Só o anúncio dele.
 *   unfilled        `data-ad-status="unfilled"`: o Google respondeu sem anúncio.
 *   script_timeout  passaram PRAZO_SCRIPT_MS e o bloco NUNCA foi processado pelo
 *                   adsbygoogle.js (sem `data-adsbygoogle-status`): o script não carregou
 *                   (bloqueador, rede, falha externa). Sem script não há anúncio possível.
 *
 * Bloco já processado e ainda sem `data-ad-status` continua `loading`: o Google pode
 * preencher, então nada o substitui.
 *
 * Conteúdo do placement (`CONTEUDOS`): `google` (loading/filled), `house_ad`
 * (unfilled/script_timeout com campanha ativa) ou `busca` (idem, sem campanha para a
 * posição). Nunca Google e anúncio interno ao mesmo tempo.
 *
 * Corrida: `filled` vence sempre, a qualquer momento. `script_timeout` não volta para
 * `loading` se o script chegar tarde e processar o bloco (evita o espaço piscar); só sai
 * para `filled` ou `unfilled`.
 */
export const PRAZO_SCRIPT_MS = 5000;
export const ESTADOS_ADSENSE = ['loading', 'filled', 'unfilled', 'script_timeout'];
export const CONTEUDOS = ['google', 'house_ad', 'busca'];

export function lerEstado(ins, { prazoEsgotado = false, anterior = 'loading' } = {}) {
    if (!ins) return anterior;
    const status = ins.getAttribute('data-ad-status');
    if (status === 'filled') return 'filled';
    if (status === 'unfilled') return 'unfilled';
    if (anterior === 'script_timeout') return 'script_timeout';
    if (prazoEsgotado && !ins.getAttribute('data-adsbygoogle-status')) return 'script_timeout';
    return 'loading';
}

/** O que o placement mostra, dado o estado do Google e se há campanha para a posição. */
export function conteudoDoPlacement({ liberado, estado, temCampanha }) {
    const semGoogle = !liberado || estado === 'unfilled' || estado === 'script_timeout';
    if (!semGoogle) return 'google';
    return temCampanha ? 'house_ad' : 'busca';
}

export function useEstadoAdsense(insRef, liberado) {
    const [estado, setEstado] = useState('loading');

    useEffect(() => {
        if (!liberado) return undefined;
        const ins = insRef.current;
        if (!ins) return undefined;
        let prazoEsgotado = false;
        let atual = 'loading';
        const conferir = () => {
            atual = lerEstado(ins, { prazoEsgotado, anterior: atual });
            setEstado(atual);
        };
        const observador = new MutationObserver(conferir);
        observador.observe(ins, { attributes: true, attributeFilter: ['data-ad-status', 'data-adsbygoogle-status'] });
        const prazo = setTimeout(() => { prazoEsgotado = true; conferir(); }, PRAZO_SCRIPT_MS);
        conferir();
        return () => { observador.disconnect(); clearTimeout(prazo); };
    }, [insRef, liberado]);

    return estado;
}
